import React, { Component } from 'react';
import styles from '../style/side-menu.module.css';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { deriveBatteryCapabilities, fetchBatteryTests } from '../utils/battery-kind';

// The examiner pages whose menu offers a battery's administration path.
const EXAMINER_BATTERY_ROUTE =
  /^\/examiner\/batteries\/([0-9a-z]*)\/(info|report|export|sessionInvite|testSession)$/;
const EXAMINER_EXAMINEE_ROUTE = /^\/examiner\/examinees\/([0-9a-z]*)\/(info|testHistory)$/;

/*
props:
- items: an ordered array of items to display in the side menu
[{
    title: string that is shown to the user
    destination: string that is pushed to history on click
    selected: bool
}, ... ]
*/
class SideMenu extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isVisible: true,
      items: [],
      /*
        null until the battery's tests are loaded. "Not known yet" is distinct
        from a resolved answer, and hides both administration entries while it
        holds — the menu must not offer the wrong path, nor a stale battery's
        path, in the gap before the fetch lands.
      */
      batteryCapabilities: null
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.createItems();
    this.loadBatteryCapabilities();
  }

  /*
    Under App.js's Switch this component happens to remount on navigation, but
    nothing in the component guarantees that, and a data fetch keyed to a route
    param cannot rely on it. Rebuild on any path change; refetch, clearing the
    previous answer first, when the battery itself changes — a stale capability
    rendered against a new battery offers the wrong administration path.
  */
  componentDidUpdate(prevProps) {
    const previousPath = prevProps.history.location.pathname;
    const currentPath = this.props.history.location.pathname;
    const previousBatteryID = prevProps.match.params.batteryID;
    const currentBatteryID = this.props.match.params.batteryID;

    if (previousBatteryID !== currentBatteryID) {
      this.setState({ batteryCapabilities: null }, () => {
        this.createItems();
        this.loadBatteryCapabilities();
      });
      return;
    }

    if (previousPath !== currentPath) this.createItems();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  /*
   * PIN-210. A battery carries no type field, so which administration path it
   * gets is only knowable from its tests' mode. This is the one place in the
   * navigation that fetches; the alternative was threading the answer down from
   * each of the four battery pages through Dashboard.
   */
  loadBatteryCapabilities = async () => {
    const location = this.props.history.location.pathname;
    if (!location.match(EXAMINER_BATTERY_ROUTE)) return;

    const batteryID = this.props.match.params.batteryID;
    if (!batteryID) return;

    const result = await fetchBatteryTests(batteryID, this.props.token);
    // A slower earlier request must not overwrite a newer battery's answer.
    if (!this.mounted || this.props.match.params.batteryID !== batteryID) return;
    this.setState({ batteryCapabilities: deriveBatteryCapabilities(result) }, this.createItems);
  };

  /*
   * The examiner's battery menu. Info, Generate Report and Export Examinee Data
   * are always present — report generation and the raw-records download are
   * separate pages (PIN-211). Exactly one administration option is offered,
   * named for what it actually does, so an examiner cannot start a rater invite
   * on a performance-based battery or an examiner-administered session on a
   * rating scale (PIN-210).
   */
  examinerBatteryItems = (batteryID, section) => {
    const items = [
      {
        title: 'Info',
        destination: `/examiner/batteries/${batteryID}/info`,
        selected: section === 'info'
      },
      {
        title: 'Generate Report',
        destination: `/examiner/batteries/${batteryID}/report`,
        selected: section === 'report'
      },
      {
        title: 'Export Examinee Data',
        destination: `/examiner/batteries/${batteryID}/export`,
        selected: section === 'export'
      }
    ];

    const capabilities = this.state.batteryCapabilities;

    if (capabilities && capabilities.canInvite) {
      items.push({
        title: 'Invite Rater',
        destination: `/examiner/batteries/${batteryID}/sessionInvite`,
        selected: section === 'sessionInvite'
      });
    }

    if (capabilities && capabilities.canCreateSession) {
      items.push({
        title: 'Create Test Session',
        destination: `/examiner/batteries/${batteryID}/testSession`,
        selected: section === 'testSession'
      });
    }

    // Sessions is gone from the top nav, so this is the only way back into a
    // session an examiner navigated away from mid-administration.
    items.push({
      title: 'Active Sessions',
      destination: '/examiner/sessions',
      selected: false
    });

    return items;
  };

  createItems = () => {
    let items = [];
    let location = this.props.history.location.pathname;
    let batteryID = this.props.match.params.batteryID;
    let examineeID = this.props.match.params.examineeID;
    let reportID = this.props.match.params.reportID;
    let tableID = this.props.match.params.tableID;
    let testID = this.props.match.params.testID;
    let itemID = this.props.match.params.itemID;

    // --- developer ---

    // battery info
    if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/info$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/info`,
          selected: true
        },
        {
          title: 'Tests',
          destination: `/developer/batteries/${batteryID}/tests`,
          selected: false
        },
        {
          title: 'Export',
          destination: `/developer/batteries/${batteryID}/export`,
          selected: false
        },
        {
          title: 'Users',
          destination: `/developer/batteries/${batteryID}/users`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/reports`,
          selected: false
        }
      ];
    }

    // battery export
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/export$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/info`,
          selected: false
        },
        {
          title: 'Tests',
          destination: `/developer/batteries/${batteryID}/tests`,
          selected: false
        },
        {
          title: 'Export',
          destination: `/developer/batteries/${batteryID}/export`,
          selected: true
        },
        {
          title: 'Users',
          destination: `/developer/batteries/${batteryID}/users`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/reports`,
          selected: false
        }
      ];
    }

    // battery test list
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/tests$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/info`,
          selected: false
        },
        {
          title: 'Tests',
          destination: `/developer/batteries/${batteryID}/tests`,
          selected: true
        },
        {
          title: 'Export',
          destination: `/developer/batteries/${batteryID}/export`,
          selected: false
        },
        {
          title: 'Users',
          destination: `/developer/batteries/${batteryID}/users`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/reports`,
          selected: false
        }
      ];
    }

    // battery users
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/users$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/info`,
          selected: false
        },
        {
          title: 'Tests',
          destination: `/developer/batteries/${batteryID}/tests`,
          selected: false
        },
        {
          title: 'Export',
          destination: `/developer/batteries/${batteryID}/export`,
          selected: false
        },
        {
          title: 'Users',
          destination: `/developer/batteries/${batteryID}/users`,
          selected: true
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/reports`,
          selected: false
        }
      ];
    }

    // item examinee content
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items\/[0-9a-z]*\/examineeContent$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/info`,
          selected: false
        },
        {
          title: 'Examinee Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examineeContent`,
          selected: true
        },
        {
          title: 'Examiner Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examinerContent`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/tables`,
          selected: false
        }
      ];
    }

    // item examiner content
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items\/[0-9a-z]*\/examinerContent$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/info`,
          selected: false
        },
        {
          title: 'Examinee Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examineeContent`,
          selected: false
        },
        {
          title: 'Examiner Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examinerContent`,
          selected: true
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/tables`,
          selected: false
        }
      ];
    }

    // item info
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items\/[0-9a-z]*\/info$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/info`,
          selected: true
        },
        {
          title: 'Examinee Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examineeContent`,
          selected: false
        },
        {
          title: 'Examiner Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examinerContent`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/tables`,
          selected: false
        }
      ];
    }

    // item tables
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items\/[0-9a-z]*\/tables$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/info`,
          selected: false
        },
        {
          title: 'Examinee Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examineeContent`,
          selected: false
        },
        {
          title: 'Examiner Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/examinerContent`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/tables`,
          selected: true
        }
      ];
    }

    // item table info
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items\/[0-9a-z]*\/tables\/[0-9a-z]*\/info$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items/${itemID}/tables/${tableID}/info`,
          selected: true
        }
      ];
    }

    // test info
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/info$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/info`,
          selected: true
        },
        {
          title: 'Items',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports`,
          selected: false
        }
      ];
    }

    // test item list
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/items$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/info`,
          selected: false
        },
        {
          title: 'Items',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items`,
          selected: true
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports`,
          selected: false
        }
      ];
    }

    // test item list
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/reports$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/info`,
          selected: false
        },
        {
          title: 'Items',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/items`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports`,
          selected: true
        }
      ];
    }

    // battery reports
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/reports$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/info`,
          selected: false
        },
        {
          title: 'Tests',
          destination: `/developer/batteries/${batteryID}/tests`,
          selected: false
        },
        {
          title: 'Export',
          destination: `/developer/batteries/${batteryID}/export`,
          selected: false
        },
        {
          title: 'Users',
          destination: `/developer/batteries/${batteryID}/users`,
          selected: false
        },
        {
          title: 'Reports',
          destination: `/developer/batteries/${batteryID}/reports`,
          selected: true
        }
      ];
    }

    // test report info
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/reports\/[0-9a-z]*\/info$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/info`,
          selected: true
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/content`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/tables`,
          selected: false
        }
      ];
    }

    // test report content
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/reports\/[0-9a-z]*\/content$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/info`,
          selected: false
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/content`,
          selected: true
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/tables`,
          selected: false
        }
      ];
    }

    // test report tables
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/reports\/[0-9a-z]*\/tables$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/info`,
          selected: false
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/content`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/tables`,
          selected: true
        }
      ];
    }

    // test report table info
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/tests\/[0-9a-z]*\/reports\/[0-9a-z]*\/tables\/[0-9a-z]*\/info$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/tests/${testID}/reports/${reportID}/tables/${tableID}/info`,
          selected: true
        }
      ];
    }

    // battery report info
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/reports\/[0-9a-z]*\/info$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/info`,
          selected: true
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/content`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/tables`,
          selected: false
        }
      ];
    }

    // battery report content
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/reports\/[0-9a-z]*\/content$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/info`,
          selected: false
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/content`,
          selected: true
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/tables`,
          selected: false
        }
      ];
    }

    // battery report tables
    else if (location.match(/^\/developer\/batteries\/[0-9a-z]*\/reports\/[0-9a-z]*\/tables$/)) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/info`,
          selected: false
        },
        {
          title: 'Content',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/content`,
          selected: false
        },
        {
          title: 'Tables',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/tables`,
          selected: true
        }
      ];
    }

    // battery report table info
    else if (
      location.match(
        /^\/developer\/batteries\/[0-9a-z]*\/reports\/[0-9a-z]*\/tables\/[0-9a-z]*\/info$/
      )
    ) {
      items = [
        {
          title: 'Info',
          destination: `/developer/batteries/${batteryID}/reports/${reportID}/tables/${tableID}/info`,
          selected: true
        }
      ];
    }

    // --- examiner ---

    // battery info / export / administration
    else if (location.match(EXAMINER_BATTERY_ROUTE)) {
      const section = location.match(EXAMINER_BATTERY_ROUTE)[2];
      items = this.examinerBatteryItems(batteryID, section);
    }

    // examinee info / test history
    else if (location.match(EXAMINER_EXAMINEE_ROUTE)) {
      const section = location.match(EXAMINER_EXAMINEE_ROUTE)[2];
      items = [
        {
          title: 'Info',
          destination: `/examiner/examinees/${examineeID}/info`,
          selected: section === 'info'
        },
        {
          title: 'Test History',
          destination: `/examiner/examinees/${examineeID}/testHistory`,
          selected: section === 'testHistory'
        },
        // Sessions is gone from the top nav, so without this an examiner sitting
        // on an examinee page has no route back to an administration they left
        // mid-flight without first entering some battery.
        {
          title: 'Active Sessions',
          destination: '/examiner/sessions',
          selected: false
        }
      ];
    }

    // no items
    else {
      this.setState({
        isVisible: false
      });
    }

    this.setState({
      items: items
    });
  };

  render() {
    if (!this.state.isVisible) return null;

    return (
      <div className={styles['container']}>
        {this.state.items.map((item) => {
          return (
            <div
              className={`${styles['item']} ${item.selected ? styles['selected'] : null}`}
              key={`item-${item.title}`}
              onClick={() => {
                this.props.history.push(item.destination);
              }}
            >
              <div
                className={`${styles['vertical-bar']} ${item.selected ? styles['selected'] : null}`}
              />
              <label className={`${styles['title']} ${item.selected ? styles['selected'] : null}`}>
                {item.title}
              </label>
            </div>
          );
        })}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  token: state.token
});

export default connect(mapStateToProps)(withRouter(SideMenu));
